import type { FeatureFlag, Workload } from "./types";

/**
 * Parses free-form text into structured workload.
 * MVP: deterministic heuristics (no API). Swap for LLM → same Workload shape.
 */
export function parseFeatureDescription(text: string): Workload {
  const lower = text.toLowerCase();
  const usersDaily = extractNumber(lower, [
    /(\d+(?:\.\d+)?)\s*k?\s*(?:users?|daily|dau)/i,
    /(\d+(?:\.\d+)?)\s*k\s*(?:users?|daily)/i,
    /daily[:\s]+(\d+(?:\.\d+)?)\s*k?/i,
  ]);
  const requestsPerUser = extractNumber(lower, [
    /(\d+)\s*(?:messages?|requests?)\s*per\s*user/i,
    /(\d+)\s*(?:messages?|req)\s*\/\s*user/i,
    /per\s*user[:\s]+(\d+)/i,
  ]);

  const features = detectFeatures(lower);

  const hasAi =
    features.includes("ai") ||
    /\bgpt\b|openai|bedrock|claude|llm|ai\s+response/i.test(lower);
  const aiShare = hasAi ? 0.35 : 0;

  return {
    usersDaily: usersDaily ?? 10_000,
    requestsPerUser: clamp(requestsPerUser ?? 100, 1, 10_000),
    enabledFeatures: features.length ? features : ["chat", "storage", "ai"],
    avgPromptTokens: hasAi ? 180 : 0,
    avgCompletionTokens: hasAi ? 320 : 0,
    aiRequestShare: aiShare,
    storageKbPerUser: features.includes("files") ? 512 : 48,
  };
}

function extractNumber(text: string, patterns: RegExp[]): number | undefined {
  for (const re of patterns) {
    const m = text.match(re);
    if (!m) continue;
    let n = parseFloat(m[1]);
    if (Number.isNaN(n)) continue;
    const segment = m[0].toLowerCase();
    if (segment.includes("k") && n < 1000) n *= 1000;
    return Math.round(n);
  }
  const generic = text.match(/(\d+(?:\.\d+)?)\s*k\b/);
  if (generic) {
    let n = parseFloat(generic[1]);
    if (!Number.isNaN(n) && n < 1000) n *= 1000;
    if (!Number.isNaN(n)) return Math.round(n);
  }
  return undefined;
}

function detectFeatures(lower: string): FeatureFlag[] {
  const out: FeatureFlag[] = [];
  if (/\bchat\b|messaging|messages?\b/.test(lower)) out.push("chat");
  if (/stor(ed|age)|persist|database|dynamo/.test(lower)) out.push("storage");
  if (/search|history|index/.test(lower)) out.push("search");
  if (/\bgpt\b|openai|bedrock|claude|llm|ai\s+response/.test(lower)) out.push("ai");
  if (/websocket|real-?time|presence/.test(lower)) out.push("realtime");
  if (/upload|file|s3|object/.test(lower)) out.push("files");
  return dedupe(out);
}

function dedupe<T>(arr: T[]): T[] {
  return [...new Set(arr)];
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}
